<!-- Loading State -->
@if (isLoading) {
  <div class="loading-container">
    <mat-progress-spinner diameter="50"></mat-progress-spinner>
    <p class="loading-text">Loading alert breakdown data...</p>
  </div>
}

<!-- Error State -->
@if (hasError && !isLoading) {
  <div class="error-container">
    <mat-card class="error-card">
      <mat-card-content>
        <div class="error-content">
          <mat-icon class="error-icon">error</mat-icon>
          <h3>Error Loading Data</h3>
          <p>{{ errorMessage }}</p>
          <button mat-raised-button color="primary" (click)="retryLoad()">
            <mat-icon>refresh</mat-icon>
            Retry
          </button>
        </div>
      </mat-card-content>
    </mat-card>
  </div>
}

<!-- Main Content -->
@if (!isLoading && !hasError) {
  <div class="alerts-breakdown-container">
    <!-- Page Header -->
    <!-- Configuration Panels -->
    <mat-accordion class="configuration-accordion">
      <!-- Chart Configuration Panel -->
      <mat-expansion-panel class="control-panel-expansion" [expanded]="false">
        <mat-expansion-panel-header>
          <mat-panel-title>
            <mat-icon>tune</mat-icon>
            Chart Configuration
          </mat-panel-title>
          <mat-panel-description>
            Customize your visualization
          </mat-panel-description>
        </mat-expansion-panel-header>
        <div class="control-panel">
          <!-- Primary Controls Row -->
          <div class="primary-controls">
            <!-- Group By Selection -->
            <mat-form-field class="control-field">
              <mat-label>Group By</mat-label>
              <mat-select
                [value]="currentState.groupBy"
                (selectionChange)="onGroupByChange($event.value)">
                @for (option of groupByOptions; track option) {
                  <mat-option [value]="option.value">
                    <div class="option-content">
                      <span class="option-label">{{ option.label }}</span>
                      <span class="option-description">{{ option.description }}</span>
                    </div>
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
            <!-- Chart Type Selection -->
            <mat-form-field class="control-field">
              <mat-label>Chart Type</mat-label>
              <mat-select
                [value]="currentState.chartType"
                (selectionChange)="onChartTypeChange($event.value)">
                <mat-select-trigger>
                  {{ getChartTypeLabel(currentState.chartType) }}
                </mat-select-trigger>
                @for (option of chartTypeOptions; track option) {
                  <mat-option [value]="option.value">
                    <div class="chart-type-option">
                      <mat-icon>{{ option.icon }}</mat-icon>
                      <span>{{ option.label }}</span>
                    </div>
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
            <!-- Time Range Selection -->
            <mat-form-field class="control-field">
              <mat-label>Time Range</mat-label>
              <mat-select
                [value]="currentState.timeRange"
                (selectionChange)="onTimeRangeChange($event.value)">
                @for (option of timeRangeOptions; track option) {
                  <mat-option [value]="option.value">
                    {{ option.label }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
            <!-- Action Buttons -->
            <div class="action-buttons">
              <button mat-raised-button color="primary" (click)="shareChart()" matTooltip="Copy shareable link">
                <mat-icon>share</mat-icon>
                Share
              </button>
            </div>
          </div>
          <!-- Chart Options Row -->
          @if (currentState.chartType === 'bar') {
            <div class="chart-options">
              <mat-form-field class="orientation-field">
                <mat-label>Orientation</mat-label>
                <mat-select
                  [value]="currentState.chartOptions?.orientation || 'vertical'"
                  (selectionChange)="onChartOptionChange('orientation', $event.value)">
                  <mat-option value="vertical">Vertical</mat-option>
                  <mat-option value="horizontal">Horizontal</mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          }
        </div>
      </mat-expansion-panel>
      <!-- Filters Panel -->
      <mat-expansion-panel class="filters-expansion" [expanded]="false">
        <mat-expansion-panel-header>
          <mat-panel-title>
            <mat-icon>filter_list</mat-icon>
            Filters
            @if (hasActiveFilters()) {
              <mat-chip-set class="active-filters">
                @if (currentState.filters.teams?.length) {
                  <mat-chip removable (removed)="onFilterChange('teams', [])">
                    Teams: {{ currentState.filters.teams?.length }}
                  </mat-chip>
                }
                @if (currentState.filters.repositories?.length) {
                  <mat-chip removable (removed)="onFilterChange('repositories', [])">
                    Repos: {{ currentState.filters.repositories?.length }}
                  </mat-chip>
                }
                @if (currentState.filters.alertTypes?.length) {
                  <mat-chip removable (removed)="onFilterChange('alertTypes', [])">
                    Alerts: {{ currentState.filters.alertTypes?.length }}
                  </mat-chip>
                }
                @if (currentState.filters.locations?.length) {
                  <mat-chip removable (removed)="onFilterChange('locations', [])">
                    Locations: {{ currentState.filters.locations?.length }}
                  </mat-chip>
                }
                @if (currentState.filters.severities?.length) {
                  <mat-chip removable (removed)="onFilterChange('severities', [])">
                    Severities: {{ currentState.filters.severities?.length }}
                  </mat-chip>
                }
              </mat-chip-set>
            }
          </mat-panel-title>
          <mat-panel-description>
            @if (hasActiveFilters()) {
              <button
                mat-button
                color="warn"
                (click)="clearAllFilters(); $event.stopPropagation()"
                >
                <mat-icon>clear</mat-icon>
                Clear All
              </button>
            }
          </mat-panel-description>
        </mat-expansion-panel-header>
        <div class="filters-grid">
          <!-- Team Filter -->
          <mat-form-field class="filter-field">
            <mat-label>Teams</mat-label>
            <mat-select
              multiple
              [value]="currentState.filters.teams || []"
              (selectionChange)="onFilterChange('teams', $event.value)">
              <div class="select-all-controls">
                <button mat-button class="select-all-btn" (click)="selectAllForFilter('teams'); $event.stopPropagation()">
                  <mat-icon>select_all</mat-icon>
                  Select All
                </button>
                <button mat-button class="select-all-btn" (click)="clearAllForFilter('teams'); $event.stopPropagation()">
                  <mat-icon>clear</mat-icon>
                  Clear All
                </button>
              </div>
              @for (team of availableTeams; track team) {
                <mat-option [value]="team">
                  {{ team }} ({{ getAlertCountForTeam(team) }})
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
          <!-- Repository Filter -->
          <mat-form-field class="filter-field">
            <mat-label>Repositories</mat-label>
            <mat-select
              multiple
              [value]="currentState.filters.repositories || []"
              (selectionChange)="onFilterChange('repositories', $event.value)">
              <div class="select-all-controls">
                <button mat-button class="select-all-btn" (click)="selectAllForFilter('repositories'); $event.stopPropagation()">
                  <mat-icon>select_all</mat-icon>
                  Select All
                </button>
                <button mat-button class="select-all-btn" (click)="clearAllForFilter('repositories'); $event.stopPropagation()">
                  <mat-icon>clear</mat-icon>
                  Clear All
                </button>
              </div>
              @for (repo of availableRepositories; track repo) {
                <mat-option [value]="repo">
                  {{ repo }} ({{ getAlertCountForRepository(repo) }})
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
          <!-- Alert Type Filter -->
          <mat-form-field class="filter-field">
            <mat-label>Alert Types</mat-label>
            <mat-select
              multiple
              [value]="currentState.filters.alertTypes || []"
              (selectionChange)="onFilterChange('alertTypes', $event.value)">
              <div class="select-all-controls">
                <button mat-button class="select-all-btn" (click)="selectAllForFilter('alertTypes'); $event.stopPropagation()">
                  <mat-icon>select_all</mat-icon>
                  Select All
                </button>
                <button mat-button class="select-all-btn" (click)="clearAllForFilter('alertTypes'); $event.stopPropagation()">
                  <mat-icon>clear</mat-icon>
                  Clear All
                </button>
              </div>
              @for (alertType of availableAlertTypes; track alertType) {
                <mat-option [value]="alertType">
                  {{ alertType }} ({{ getAlertCountForAlertType(alertType) }})
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
          <!-- Systems Filter -->
          <mat-form-field class="filter-field">
            <mat-label>Systems</mat-label>
            <mat-select
              multiple
              [value]="currentState.filters.systems || []"
              (selectionChange)="onFilterChange('systems', $event.value)">
              <div class="select-all-controls">
                <button mat-button class="select-all-btn" (click)="selectAllForFilter('systems'); $event.stopPropagation()">
                  <mat-icon>select_all</mat-icon>
                  Select All
                </button>
                <button mat-button class="select-all-btn" (click)="clearAllForFilter('systems'); $event.stopPropagation()">
                  <mat-icon>clear</mat-icon>
                  Clear All
                </button>
              </div>
              @for (system of availableSystems; track system) {
                <mat-option [value]="system">
                  {{ system }} ({{ getAlertCountForSystem(system) }})
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
          <!-- Severity Filter -->
          <mat-form-field class="filter-field">
            <mat-label>Severities</mat-label>
            <mat-select
              multiple
              [value]="currentState.filters.severities || []"
              (selectionChange)="onFilterChange('severities', $event.value)">
              <div class="select-all-controls">
                <button mat-button class="select-all-btn" (click)="selectAllForFilter('severities'); $event.stopPropagation()">
                  <mat-icon>select_all</mat-icon>
                  Select All
                </button>
                <button mat-button class="select-all-btn" (click)="clearAllForFilter('severities'); $event.stopPropagation()">
                  <mat-icon>clear</mat-icon>
                  Clear All
                </button>
              </div>
              @for (severity of availableSeverities; track severity) {
                <mat-option [value]="severity">
                  {{ severity }} ({{ getAlertCountForSeverity(severity) }})
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>
      </mat-expansion-panel>
    </mat-accordion>
    <!-- Chart Display -->
    <mat-card class="chart-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>bar_chart</mat-icon>
          Visualization
        </mat-card-title>
        <mat-card-subtitle>
          {{ filteredDataPoints.length }} data points •
          Grouped by {{ getGroupByLabel(currentState.groupBy) }}
        </mat-card-subtitle>
      </mat-card-header>
      <mat-card-content>
        <div class="chart-container">
          @if (isLoading) {
            <mat-progress-bar mode="indeterminate"></mat-progress-bar>
          }
          @if (!isLoading) {
            <canvas
              baseChart
              [type]="chartType"
              [data]="chartData"
              [options]="chartOptions"
              class="chart-canvas">
            </canvas>
          }
        </div>
      </mat-card-content>
    </mat-card>
  </div>
}

<!-- Fixed Footer with Summary Stats -->
@if (filteredDataPoints.length > 0) {
  <div class="summary-footer">
    <div class="summary-cards">
      <div class="summary-card">
        <mat-icon class="summary-icon">notifications</mat-icon>
        <div class="summary-details">
          <div class="summary-value">{{ getTotalAlerts() }} Total</div>
        </div>
      </div>
      <div class="summary-card">
        <mat-icon class="summary-icon">category</mat-icon>
        <div class="summary-details">
          <div class="summary-value">{{ getUniqueAlertTypes() }} Types</div>
        </div>
      </div>
      <div class="summary-card">
        <mat-icon class="summary-icon">groups</mat-icon>
        <div class="summary-details">
          <div class="summary-value">{{ getUniqueTeams() }} Teams</div>
        </div>
      </div>
      <div class="summary-card">
        <mat-icon class="summary-icon">folder</mat-icon>
        <div class="summary-details">
          <div class="summary-value">{{ getUniqueRepositories() }} Repositories</div>
        </div>
      </div>
    </div>
  </div>
}
